/* ============================================================================
   Cart ajax "sections" fix. This store's Markets URL structure prefixes every
   locale (/en-pl/, /pl-pl/), so the bare root "/" 301-redirects. Shopify's
   bundled section rendering inside the cart endpoints (cart/add|change|update
   .js with a "sections" param) renders against "/" unless a sections_url is
   sent, does not follow the redirect, and returns "sections": null — which
   crashes theme.js CartDrawer.onCartChange_fn, so the drawer never opens or
   updates after add-to-cart. theme.js never sends sections_url (verified
   against the live store and the Maestrooo demo, 2026-06-12), so inject it
   here for every cart ajax call that bundles sections.
   ========================================================================= */
(function () {
  var origFetch = window.fetch;
  window.fetch = function (input, init) {
    try {
      var url = typeof input === 'string' ? input : (input && input.url) || '';
      if (/\/cart\/(?:add|change|update|clear)\.js/.test(url) && init && init.body) {
        var root =
          (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
        if (init.body instanceof FormData) {
          if (init.body.has('sections') && !init.body.has('sections_url')) {
            init.body.set('sections_url', root);
          }
        } else if (typeof init.body === 'string' && init.body.charAt(0) === '{') {
          var data = JSON.parse(init.body);
          if (data.sections && !data.sections_url) {
            data.sections_url = root;
            init.body = JSON.stringify(data);
          }
        }
      }
    } catch (e) {
      /* never block the actual request */
    }
    return origFetch.call(this, input, init);
  };
})();

/**
 * Extends reading-text sections that have a second split-lines[preserve-letters]
 * (the "paragraph_text" field) to include those letters in the scroll animation.
 *
 * The built-in reading-text component only looks at the FIRST split-lines via
 * querySelector (singular). This script watches for the scroll height being set
 * (meaning reading-text has done its layout pass), then re-applies the full
 * ViewTimeline animation over ALL split-lines chars combined.
 */
(function () {
  function initReadingTextExtension(readingTextEl) {
    const allSplitLines = Array.from(
      readingTextEl.querySelectorAll('split-lines[preserve-letters]')
    );
    if (allSplitLines.length <= 1) return;
    // Idempotency: section:load in the theme editor re-runs init over elements
    // that may already carry the style MutationObserver — never stack a second.
    if (readingTextEl.dataset.rtParagraphExtended) return;
    readingTextEl.dataset.rtParagraphExtended = 'true';

    const startOpacity = parseFloat(
      readingTextEl.getAttribute('text-start-opacity') || '0.2'
    );
    const readingSpeed = parseFloat(
      readingTextEl.getAttribute('reading-speed') || '1.5'
    );
    const lettersToSimultaneouslyReveal = 15;
    let applying = false;

    function applyFullAnimation() {
      if (!window.ViewTimeline) return;

      const allChars = allSplitLines.flatMap((sl) => {
        if (!sl || !sl.shadowRoot) return [];
        const lines = Array.from(sl.lines);
        return lines.flatMap((line) => Array.from(line.children));
      });

      if (!allChars.length) return;

      applying = true;
      readingTextEl.style.setProperty(
        '--reading-text-scroll-height',
        `${readingSpeed * allChars.length}vh`
      );
      requestAnimationFrame(() => {
        applying = false;
      });

      const stickyHeight =
        getComputedStyle(readingTextEl)
          .getPropertyValue('--sticky-area-height')
          .trim() || '0px';
      const timeline = new ViewTimeline({
        subject: readingTextEl,
        axis: 'block',
        inset: `${stickyHeight} 0px`,
      });

      const button = readingTextEl.querySelector('.button');
      const items = button ? [...allChars, button] : allChars;
      const step =
        100 / (allChars.length + lettersToSimultaneouslyReveal);

      items.forEach((item, index) => {
        item.getAnimations?.().forEach((a) => a.cancel());
        item.animate(
          { opacity: [startOpacity, 1] },
          {
            fill: 'both',
            timeline,
            rangeStart: `contain ${index * step}%`,
            rangeEnd: `contain ${
              (index + lettersToSimultaneouslyReveal + 1) * step
            }%`,
          }
        );
      });
    }

    // reading-text sets --reading-text-scroll-height via inline style after layout.
    // Watch for that to know when it's safe to access letter positions and re-apply.
    new MutationObserver(() => {
      if (!applying) applyFullAnimation();
    }).observe(readingTextEl, {
      attributes: true,
      attributeFilter: ['style'],
    });
  }

  function init() {
    document.querySelectorAll('reading-text').forEach(initReadingTextExtension);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Theme editor: a saved section is re-injected as fresh DOM; this external
  // file doesn't re-execute, so re-extend any reading-text in the new markup.
  document.addEventListener('shopify:section:load', function (event) {
    event.target
      .querySelectorAll('reading-text')
      .forEach(initReadingTextExtension);
  });
})();

/* ============================================================================
   iOS status-bar fix. iOS 26 Safari paints an opaque band over the status bar
   (instead of compositing page pixels behind it, like on google.com) whenever
   a semantic <header> element's box sits near the viewport top - regardless
   of position, background, or contents (device-bisected 2026-06-10: an empty
   zero-height <header> still triggers it). The theme's header section wrapper
   is a <header> tag; custom.css dissolves its box with display:contents under
   html.ios-status-bar-fix, which this script sets on iOS. This controller
   then re-pins height-observer (a non-semantic element - safe to stick) only
   while the header actually floats: when the theme reveals it mid-scroll
   (is-hidden removed) or on upward scroll once it has left the viewport.
   While pinned, iOS extends the solid header surface into the status bar -
   the correct look. Unpin near the top and shortly after the theme hides it
   again. iOS-only; other platforms keep the theme's normal sticky behavior.
   ========================================================================= */
(function () {
  var isIOS =
    /iPhone|iPad|iPod/.test(navigator.userAgent) ||
    (navigator.userAgent.indexOf('Macintosh') !== -1 && navigator.maxTouchPoints > 1);
  if (!isIOS) return;

  function init() {
    var wrapper = document.querySelector('.shopify-section--header');
    var header = wrapper && wrapper.querySelector('.header');
    // The wrapper gets display:contents (no box), so all measuring happens
    // on height-observer - the element that also carries the sticky pin.
    var measureEl = wrapper && (wrapper.querySelector('height-observer') || header);
    if (!wrapper || !header || !measureEl) return;

    document.documentElement.classList.add('ios-status-bar-fix');

    var unpinTimer = null;
    function pin() {
      clearTimeout(unpinTimer);
      wrapper.classList.add('header-repinned');
    }
    function unpin() {
      clearTimeout(unpinTimer);
      wrapper.classList.remove('header-repinned');
    }
    // Let the theme's 0.2s slide-out finish while still pinned.
    function unpinSoon() {
      clearTimeout(unpinTimer);
      unpinTimer = setTimeout(unpin, 250);
    }

    // React only to is-hidden transitions (is-solid also mutates the class
    // attribute and must not cause pinning while scrolling down from top).
    var wasHidden = header.classList.contains('is-hidden');
    new MutationObserver(function () {
      var nowHidden = header.classList.contains('is-hidden');
      if (nowHidden === wasHidden) return;
      wasHidden = nowHidden;
      if (nowHidden) unpinSoon();
      else if (window.scrollY > 2) pin();
    }).observe(header, { attributes: true, attributeFilter: ['class'] });

    var lastY = window.scrollY;
    window.addEventListener(
      'scroll',
      function () {
        var y = window.scrollY;
        var goingUp = y < lastY;
        lastY = y;
        if (y <= 2) {
          unpin();
          return;
        }
        // Slow upward scroll that never triggered the theme's hide cycle:
        // once the header has fully left the viewport, pin it back in.
        if (
          goingUp &&
          !wrapper.classList.contains('header-repinned') &&
          !header.classList.contains('is-hidden') &&
          measureEl.getBoundingClientRect().bottom < 0
        ) {
          pin();
        }
      },
      { passive: true }
    );
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

/* ============================================================================
   Equal-width color swatch grid. custom.css lays out the variant picker's
   block swatches (the ones with a color tile) as a grid of equal columns
   sized by --swatch-col-width. The column width must equal the widest label,
   which CSS alone cannot derive for an auto-fill grid: free space is
   distributed equally across tracks, so one long translated label (PL
   "Pomarańczowy") overflows its track. Measure the widest swatch per list
   and set the variable. Re-runs after fonts load and when new pickers are
   injected (quick view / drawers).
   ========================================================================= */
(function () {
  var LIST_SELECTOR = '.variant-picker__option-values';

  function sizeList(list) {
    if (!list.querySelector(':scope > .block-swatch > .block-swatch__color')) return;
    var swatches = list.querySelectorAll(':scope > .block-swatch');
    var previous = [];
    swatches.forEach(function (swatch, i) {
      previous[i] = swatch.style.width;
      swatch.style.width = 'max-content';
    });
    var max = 0;
    swatches.forEach(function (swatch) {
      max = Math.max(max, swatch.getBoundingClientRect().width);
    });
    swatches.forEach(function (swatch, i) {
      swatch.style.width = previous[i];
    });
    if (max > 0) {
      list.style.setProperty('--swatch-col-width', Math.ceil(max) + 'px');
    }
  }

  function sizeAll() {
    document.querySelectorAll(LIST_SELECTOR).forEach(sizeList);
  }

  var queued = false;
  function queueSizeAll() {
    if (queued) return;
    queued = true;
    requestAnimationFrame(function () {
      queued = false;
      sizeAll();
    });
  }

  function init() {
    sizeAll();

    if (document.fonts) {
      document.fonts.ready.then(queueSizeAll);
    }

    // Pickers rendered later (quick view, drawers) and hidden-at-inject
    // content that measures 0 until shown.
    new MutationObserver(function (mutations) {
      for (var m = 0; m < mutations.length; m++) {
        var added = mutations[m].addedNodes;
        for (var n = 0; n < added.length; n++) {
          var node = added[n];
          if (node.nodeType !== 1) continue;
          if (node.matches(LIST_SELECTOR) || node.querySelector(LIST_SELECTOR)) {
            queueSizeAll();
            return;
          }
        }
      }
    }).observe(document.body, { childList: true, subtree: true });

    document.addEventListener('dialog:after-show', queueSizeAll);

    // Label width depends on viewport font size (mobile/desktop breakpoints)
    var resizeTimer;
    window.addEventListener(
      'resize',
      function () {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(sizeAll, 150);
      },
      { passive: true }
    );
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

/* ============================================================================
   Hero "Configure & buy" CTA -> smooth-scroll to the product buy box.

   In a JSON template the main section's real DOM id is generated as
   shopify-section-template--<theme_id>__main, NOT shopify-section-main, so a
   hardcoded #shopify-section-main anchor in the editor URL field matches
   nothing and the click does nothing. Resolve the real main-product section at
   click time and scroll to it, offset by the live header height so the buy box
   isn't hidden under the sticky header. Matches the current editor value and a
   cleaner #buy-box alias.
   ========================================================================= */
(function () {
  var TRIGGER = 'a[href="#shopify-section-main"], a[href="#buy-box"]';

  document.addEventListener('click', function (event) {
    if (!event.target || !event.target.closest) return;

    var trigger = event.target.closest(TRIGGER);
    if (!trigger) return;

    var target =
      document.querySelector('.shopify-section--main-product') ||
      document.querySelector('[id^="shopify-section"][id*="__main"]') ||
      document.getElementById('buy-box');
    if (!target) return;

    event.preventDefault();

    var header = document.querySelector('.header');
    var offset = header ? header.getBoundingClientRect().height : 0;
    var top =
      target.getBoundingClientRect().top + window.pageYOffset - offset - 12;

    window.scrollTo({ top: top, behavior: 'smooth' });
  });
})();

/* ============================================================================
   Shipping estimator: honest message for Poland.

   Poland is intentionally excluded from the manual shipping zones because
   delivery there is handled by the InPost integration, which only surfaces
   its options (Paczkomat, courier) inside checkout. Shopify's cart estimate
   API (/cart/async_shipping_rates.json) never returns pickup-point /
   checkout-app rates, so for Poland it returns an empty list and the theme
   prints "Sorry, we do not ship to your address" — wrong and alarming.

   theme.js's <shipping-estimator> writes results via innerHTML on
   .shipping-estimator__results; a zero-rate render is a <p> plus an empty
   <ul> (no <li>). Watch those renders document-wide (survives cart-drawer
   re-renders) and, when the selected country is Poland and no rates came
   back, swap in an informative message instead. The country <option> value
   is always the English name "Poland" on every locale.
   ========================================================================= */
(function () {
  var MESSAGES = {
    pl: 'Dostawę do Polski realizujemy przez InPost (Paczkomat lub kurier). Opcje i ceny dostawy zobaczysz przy finalizacji zamówienia.',
    en: 'Shipping to Poland is handled by InPost (Paczkomat lockers or courier). Delivery options and prices are shown at checkout.'
  };

  var observer = new MutationObserver(function (mutations) {
    for (var i = 0; i < mutations.length; i++) {
      var target = mutations[i].target;
      if (
        !target.classList ||
        !target.classList.contains('shipping-estimator__results')
      ) {
        continue;
      }

      if (target.querySelector('li')) continue; // rates (or errors) present

      var paragraph = target.querySelector('p');
      if (!paragraph) continue;

      var estimator = target.closest('shipping-estimator');
      var country =
        estimator &&
        estimator.querySelector('[name="address[country]"]');
      if (!country || country.value !== 'Poland') continue;

      var locale = (window.Shopify && window.Shopify.locale) || 'en';
      paragraph.textContent =
        MESSAGES[locale.split('-')[0]] || MESSAGES.en;
    }
  });

  observer.observe(document.body, { childList: true, subtree: true });
})();
